import React, { useState, useEffect } from "react";
import { ref, onValue, get } from "firebase/database";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { FiBook, FiCheckCircle, FiCircle } from "react-icons/fi";

const Chapters = () => {
  const [chapters, setChapters] = useState([]);
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [courseProgress, setCourseProgress] = useState(0);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          setError("User not authenticated. Please log in.");
          setLoading(false);
          return;
        }

        const email = currentUser.email;
        let foundUser = null;
        let userCourse = null;

        // First check if user is admin/assistant in users collection
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);

        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          foundUser = Object.values(usersData).find(
            (u) => u.email === email
          );
          if (foundUser) {
            userCourse = foundUser.enrolledCourse;
          }
        }

        // If not found in users collection, search in courses/students
        if (!foundUser) {
          const coursesRef = ref(db, "courses");
          const coursesSnapshot = await get(coursesRef);

          if (coursesSnapshot.exists()) {
            const courses = coursesSnapshot.val();

            // Search through each course's students
            for (const [courseId, courseData] of Object.entries(courses)) {
              if (courseData.students) {
                const student = Object.values(courseData.students).find(
                  (s) => s.email === email
                );
                if (student) {
                  foundUser = student;
                  userCourse = courseId;
                  break;
                }
              }
            }
          }
        }

        if (foundUser && userCourse) {
          setEnrolledCourse(userCourse);
        } else {
          setError("No enrolled course found. Please enroll in a course.");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setError("Failed to fetch user data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (!enrolledCourse) return; // Exit if no enrolled course

    const chaptersRef = ref(db, `courses/${enrolledCourse}/chapters`);

    // Fetch chapters for the enrolled course
    const unsubscribeChapters = onValue(
      chaptersRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const chaptersArray = Object.keys(data).map((key) => ({
            id: key,
            name: data[key].name,
            completed: data[key].done || false,
            description: data[key].description || "",
            order: data[key].order || 0
          })).sort((a, b) => a.order - b.order); // Sort chapters by order

          setChapters(chaptersArray);
        } else {
          setError("No chapters found for the enrolled course.");
        }
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching chapters:", error);
        setError("Failed to fetch chapters. Please try again later.");
        setLoading(false);
      }
    );

    return () => unsubscribeChapters(); // Cleanup chapters listener
  }, [enrolledCourse]); // Re-fetch when enrolledCourse changes

  // Update progress dynamically
  useEffect(() => {
    if (chapters.length > 0) {
      const completedChapters = chapters.filter((ch) => ch.completed).length;
      const totalChapters = chapters.length;
      const newProgress = Math.round((completedChapters / totalChapters) * 100);
      setCourseProgress(newProgress);
    }
  }, [chapters]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="bg-red-500/10 p-6 rounded-xl w-full max-w-md text-center border border-red-500/20">
          <div className="text-red-400 font-medium text-lg mb-2">Error</div>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 py-6 w-full">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-teal-600 to-sky-600 rounded-2xl p-6 md:p-8 shadow-xl mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Course Chapters</h1>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-teal-100 text-sm md:text-base">Progress:</span>
            <span className="text-white font-bold text-lg md:text-xl">{courseProgress}%</span>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 mb-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center text-sm text-gray-400">
            <span>Overall Progress</span>
            <span>{courseProgress}%</span>
          </div>
          <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-sky-500 rounded-full transition-all duration-500"
              style={{ width: `${courseProgress}%` }}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-gray-700/30 rounded-lg p-3">
              <div className="text-sm text-gray-400 mb-1">Completed</div>
              <div className="text-xl font-semibold text-white">
                {chapters.filter(ch => ch.completed).length}
              </div>
            </div>
            <div className="bg-gray-700/30 rounded-lg p-3">
              <div className="text-sm text-gray-400 mb-1">Total</div>
              <div className="text-xl font-semibold text-white">
                {chapters.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chapters List */}
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        {chapters.length > 0 ? (
          chapters.map((chapter) => (
            <div
              key={chapter.id}
              className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden hover:border-teal-500/30 transition-all duration-300"
            >
              <div className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center text-teal-400">
                    <FiBook className="text-xl" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-white mb-1">{chapter.name}</h3>
                      <div className="flex items-center space-x-2">
                        {chapter.completed ? (
                          <FiCheckCircle className="text-green-500 text-xl" />
                        ) : (
                          <FiCircle className="text-gray-500 text-xl" />
                        )}
                      </div>
                    </div>
                    {chapter.description && (
                      <p className="text-sm text-gray-400">{chapter.description}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-8 text-center">
            <FiBook className="mx-auto text-4xl text-gray-400 mb-4" />
            <p className="text-gray-400 text-lg">No chapters found</p>
            <p className="text-gray-500 mt-2">Please check back later for updates</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chapters;